// web/src/player/RoastCharacter.tsx
import { useRive, Layout, Fit, Alignment } from "@rive-app/react-webgl2";
import { useEffect, useImperativeHandle, forwardRef, useRef } from "react";
import type { Mode, CharacterHandle } from "./types";

// Both modes are full-frame artboards driven by SM_Main + an auto-bound VM
// exposing Mouth/Actions/Censor/State. The toast artboard is `mrtoast` (NOT
// `mrtoast_character`/`mrtoast_host`, which carry SM_Toast) — verified live.
export const RIVE_ASSETS = {
  roast: { artboard: "mrroast", stateMachine: "SM_Main", viewModel: "MrRoast" },
  toast: { artboard: "mrtoast", stateMachine: "SM_Main", viewModel: "MrToast" },
} as const;

/** Decks render in a 16:9 stage; each character has a dedicated WIDE artboard
 *  (`mrroast_wide` / `mrtoast_wide`) authored into the SAME .riv, driven by the SAME
 *  SM_Main — only the artboard differs (the portrait artboard would pillarbox in 16:9). */
export function riveArtboard(mode: Mode, wide: boolean): string {
  const base = RIVE_ASSETS[mode].artboard;
  return wide ? `${base}_wide` : base;
}

export const RoastCharacter = forwardRef<CharacterHandle, { src: string; mode: Mode; wide?: boolean }>(
  ({ src, mode, wide }, ref) => {
    const a = RIVE_ASSETS[mode];
    // autoBind:true — Rive auto-binds the artboard's default VM instance, so
    // RIVE_ASSETS.viewModel is informational only and is NOT passed to useRive.
    const { rive, RiveComponent, canvas } = useRive({
      src, artboard: riveArtboard(mode, !!wide), stateMachines: a.stateMachine, autoBind: true, autoplay: true,
      // Full-frame iOS artboard (9:16): contain + center fills the 9:16 stage.
      layout: new Layout({ fit: Fit.Contain, alignment: Alignment.Center }),
    });
    const props = useRef<{ mouth: any; emotion: any; censor: any; state: any } | null>(null);
    // Track the live canvas so the video-export compositor can drawImage it. The
    // @rive-app/react-webgl2 runtime blits its shared GL render onto this 2D canvas,
    // so it is reliably readable via ctx.drawImage (proven in the export spike).
    const canvasRef = useRef<HTMLCanvasElement | null>(null);
    canvasRef.current = canvas;
    useEffect(() => {
      const vmi = rive?.viewModelInstance;
      if (!vmi) return;
      props.current = { mouth: vmi.number("Mouth"), emotion: vmi.enum("Actions"), censor: vmi.boolean("Censor"), state: vmi.enum("State") };
    }, [rive]);

    // Keep the WebGL drawing surface in lockstep with the window. useRive enables
    // shouldResizeCanvasToContainer by default (an internal container ResizeObserver),
    // but we ALSO handle window 'resize' explicitly. Coalesce bursts into one rAF so
    // we resize the backing buffer at most once per frame AND read the canvas size
    // after layout has settled (avoids per-event buffer resets / stale reads).
    // DPR clamped to [1,3] to match the runtime's internal getDevicePixelRatio().
    useEffect(() => {
      if (!rive) return;
      let raf = 0;
      const onResize = () => {
        cancelAnimationFrame(raf);
        raf = requestAnimationFrame(() =>
          rive.resizeDrawingSurfaceToCanvas(Math.min(Math.max(window.devicePixelRatio || 1, 1), 3)),
        );
      };
      window.addEventListener("resize", onResize);
      return () => { cancelAnimationFrame(raf); window.removeEventListener("resize", onResize); };
    }, [rive]);

    useImperativeHandle(ref, () => ({
      // Guard EACH property, not just `p`: a view-model may omit some inputs (e.g. the
      // MrToast VM has no `Censor`), and `p.censor.value` on a null prop throws inside
      // the rAF tick — which aborts tick() before it reschedules, freezing the whole
      // character (mouth, captions, slides, zoom). Per-property guards make it no-op.
      setMouth: (v) => { const p = props.current; if (p?.mouth && p.mouth.value !== v) p.mouth.value = v; },
      setEmotion: (e) => { const p = props.current; if (p?.emotion && p.emotion.value !== e) p.emotion.value = e; },
      setCensor: (b) => { const p = props.current; if (p?.censor && p.censor.value !== b) p.censor.value = b; },
      setState: (v) => { const p = props.current; if (p && p.state && p.state.value !== v) { try { p.state.value = v; } catch { /* invalid enum value — ignore */ } } },
      getCanvas: () => canvasRef.current,
      setCaptureResolution: (dims) => {
        if (!rive) return;
        try {
          if (dims) {
            const el = canvasRef.current;
            const cssW = el?.clientWidth || dims.w;
            // pick a DPR that makes the backing buffer at least the export width
            const dpr = Math.max(1, Math.ceil(dims.w / cssW));
            rive.resizeDrawingSurfaceToCanvas(dpr);
          } else {
            // restore the normal clamped window DPR
            rive.resizeDrawingSurfaceToCanvas(Math.min(Math.max(window.devicePixelRatio || 1, 1), 3));
          }
        } catch { /* ignore — never disturb playback */ }
      },
    }), [rive]);

    return <RiveComponent style={{ width: "100%", height: "100%" }} />;
  }
);
RoastCharacter.displayName = "RoastCharacter";
